<template>
    <div id="mentor-request-form" ref="mentor-request-form">
        <template v-if="errorMessage">
            {{ errorMessage }}
        </template>
        <template v-if="isLoading">
            <div class="container container--sm">
                <loader-cluster variant="onboarding-progress"/>
                <loader-cluster variant="mentor-request"/>
            </div>
        </template>
        <template v-else>
            <template v-if="formStep === 1">
                <alert
                    type="danger"
                    :title="$t('error_message.mentor_request.alert_title', 'Oops. Your venture profile is not yet complete.')"
                    class="mb-7"
                    v-if="requirementAlertData"
                    :cta-text="requirementAlertData.cta ? requirementAlertData.cta.text : ''"
                    :cta-url="requirementAlertData.cta ? requirementAlertData.cta.url : ''"
                >
                    <ul
                        class="text-secondary"
                        :class="requirementAlertData.cta ? 'mb-5' : 'mb-0'"
                        v-for="message in requirementAlertData.message"
                    >
                        <li v-html="message"/>
                    </ul>
                </alert>
                <div class="mentorship-requirements">
                    <div class="mentors-request__requirement-list">
                        <div class="mentors-request__requirement">
                            <div class="mentors-request__requirement-number">
                                <div class="label">1.</div>
                            </div>
                            <div class="mentors-request__requirement-content-wrapper">
                                <div class="mentors-request__requirement-content">
                                    <div class="label mb-2">{{ $t('vc4a_account') }}</div>
                                    <div class="paragraph mentors-request__requirement-hint">
                                        {{ userIsSignedIn ? $t('mentor_request.requirement_1_success','You’re signed in.') : $t('mentor_request.requirement_1', 'Sign in or create a VC4A account to continue.') }}
                                    </div>
                                </div>
                                <div class="mentors-request__requirement-action">
                                    <div v-if="userIsSignedIn">
                                        <icon name="checkmark-circle" class="text-success w-4 h-4" />
                                    </div>
                                    <a :href="loginUrl" class="btn btn--secondary" v-else>
                                        {{ $t('get_started') }}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div class="mentors-request__requirement">
                            <div class="mentors-request__requirement-number">
                                <div class="label">2.</div>
                            </div>
                            <div class="mentors-request__requirement-content-wrapper" v-if="!loadingVentures && userHasVentureProfile">
                                <div class="mentors-request__requirement-content">
                                    <div class="label mb-2">{{ $t('mentor_request.select_venture') }}</div>
                                    <div class="form-radio my-5" v-for="(venture, index) in ventures">
                                        <input type="radio" class="form-radio__input" :id="venture.id" :value="venture.venture_id" v-model="mentorRequest.venture_id">
                                        <label :for="venture.id" class="form-radio__label">
                                            {{ venture.title }} -
                                            <a :href="venture.permalink">{{ $t('single_request.view_venture_profile') }}</a>
                                        </label>
                                    </div>
                                    <div class="form-radio">
                                        <input type="radio" class="form-radio__input" id="other-venture" :value="0" v-model="mentorRequest.venture_id">
                                        <label for="other-venture" class="form-radio__label">
                                            {{ $t('other') }} -
                                            <a :href="getNewVentureUrl" target="_blank">{{ $t('add_venture') }}</a>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="mentors-request__requirement-content-wrapper" v-else>
                                <div class="mentors-request__requirement-content" >
                                    <div class="label mb-2">{{ $t('venture_profile') }}</div>
                                    <div class="paragraph mentors-request__requirement-hint">{{ $t('mentor_request.requirement_2') }}</div>
                                    <div class="paragraph mentors-request__requirement-hint">{{ $t('mentor_request.requirement_3') }}</div>
                                </div>
                                <div class="mentors-request__requirement-action">
                                    <a :href="getNewVentureUrl" class="btn btn--secondary" target="_blank">{{ $t('add_venture') }}</a>
                                </div>
                            </div>
                        </div>
                        <div class="mentors-request__requirement" v-if="disallowedVenturesList.length">
                            <div class="mentors-request__requirement-number">
                                <div class="label"></div>
                            </div>
                            <div class="mentors-request__requirement-content-wrapper">
                                <div class="mentors-request__requirement-content" >
                                    <div class="label mb-2">{{ $t('mentor_request.ventures_not_allowed') }}</div>
                                    <div class="form-radio my-5" v-for="(venture, index) in disallowedVenturesList">
                                        <label class="form-radio__label disabled">
                                            {{ venture.title }} -
                                            <a :href="venture.permalink">{{ $t('single_request.view_venture_profile') }}</a>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="formStep === 2">
                <div class="mentors-request__details">
                    <label>{{ $t('mentor_request.topic.label') }}</label>
                    <div class="paragraph-sm text-tertiary"> {{ $t('mentor_request.topic.hint') }}</div>
                    <vc4a-select
                        :selected="mentorTerms.topic"
                        :placeholder="$t('select_placeholder_multiple', '--select--')"
                        :options="topic"
                        track-by="item_id"
                        label="getTermLabel"
                        :min="3"
                        :max="3"
                        @onInput="mentorTerms.topic = $event"
                    />
                    <vuelidate-error
                        type="required"
                        :name="$t('topic')"
                        :model="v$.mentorTerms.topic"
                    />
                </div>
            </template>
            <template v-else-if="formStep === 3">
                <div class="mentors-request__introduction">
                    <div class="grid cols-2@md">
                        <div class="mb-6">
                            <label for="request-title">{{ $t('mentor_request.request_title_label') }}</label>
                            <input
                                type="text"
                                id="request-title"
                                :placeholder="$t('mentor_request.request_title_placeholder')"
                                :class="['form-field',{'form-field--invalid': v$.mentorRequest.title.$error}]"
                                :maxlength="titleMaxLength"
                                v-model="mentorRequest.title"
                                @blur="v$.mentorRequest.title.$touch()"
                            />
                            <div class="mentors-request__input-counter paragraph-sm">{{ mentorRequest.title.length + ' / ' + titleMaxLength }}</div>
                            <vuelidate-error
                                :type="['required']"
                                :name="$t('mentor_request.request_title_label')"
                                :model="v$.mentorRequest.title"
                            />
                        </div>
                    </div>
                    <div class="mb-6">
                        <label for="mentor-introduction">{{ $t('mentor_request.intro_to_mentor.label') }}</label>
                        <div class="mentors-request__form-hint paragraph-sm">
                            <div class="mb-3">{{ $t('mentor_request.intro_to_mentor.hint') }}</div>
                            <a href="#" class="flex mb-3" @click.prevent="showIntroExample = true">
                                <icon name="question-mark-circle" class-name="mentors-request__form-hint-icon"/>
                                {{ $t('see_example') }}
                            </a>
                        </div>
                        <modal :show="showIntroExample" @close="showIntroExample = false">
                            <template #title><slot name="introduction-modal-title"/></template>
                            <template #content><slot name="introduction-modal-content"/></template>
                        </modal>
                        <textarea
                            :class="['form-field',{'form-field--invalid': v$.mentorMeta.introduction.$error}]"
                            id="mentor-introduction"
                            rows="4"
                            v-model="mentorMeta.introduction"
                            @blur="blurInput('introduction')"
                        />
                        <div class="mentors-request__input-counter paragraph-sm">{{ mentorMeta.introduction.length + ' / ' + textareaMaxLength }}</div>
                        <vuelidate-error
                            :type="['required', 'minLength']"
                            :name="$t('mentor_request.intro_to_mentor.label')"
                            :model="v$.mentorMeta.introduction"
                        />
                    </div>
                    <div class="mb-6">
                        <label for="mentor-problem">{{ $t('mentor_request.problem.label') }}</label>
                        <div class="mentors-request__form-hint paragraph-sm">
                            <div class="mb-3 w-full">{{ $t('mentor_request.problem.hint') }}</div>
                            <a href="#" class="flex mb-3" @click.prevent="showProblemExample = true">
                                <icon name="question-mark-circle" class-name="mentors-request__form-hint-icon"/>
                                {{ $t('see_example') }}
                            </a>
                        </div>
                        <textarea
                            :class="['form-field',{'form-field--invalid': v$.mentorMeta.problem.$error}]"
                            id="mentor-problem"
                            rows="4"
                            v-model="mentorMeta.problem"
                            :maxlength="textareaMaxLength"
                            @blur="blurInput('problem')"
                        />
                        <div class="mentors-request__input-counter paragraph-sm">{{ mentorMeta.problem.length + ' / ' + textareaMaxLength }}</div>
                        <vuelidate-error
                            :type="['required', 'minLength']"
                            :name="$t('mentor_request.problem.label')"
                            :model="v$.mentorMeta.problem"
                        />
                        <modal :show="showProblemExample" @close="showProblemExample = false">
                            <template #title><slot name="problem-modal-title"/></template>
                            <template #content><slot name="problem-modal-content"/></template>
                        </modal>
                    </div>
                    <div class="mb-6">
                        <label for="request">{{ $t('mentor_request.request.label') }}</label>
                        <div class="mentors-request__form-hint paragraph-sm">
                            <div class="mb-3 w-full">{{ $t('mentor_request.request.hint') }}</div>
                            <a href="#" class="flex mb-3" @click.prevent="showRequestExample = true">
                                <icon name="question-mark-circle" class-name="mentors-request__form-hint-icon"/>
                                {{ $t('see_example') }}
                            </a>
                        </div>
                        <textarea
                            :class="['form-field',{'form-field--invalid': v$.mentorRequest.content.$error}]"
                            id="request"
                            rows="4"
                            v-model="mentorRequest.content"
                            :maxlength="textareaMaxLength"
                            @blur="blurInput('request')"
                        />
                        <div class="mentors-request__input-counter paragraph-sm">{{ mentorRequest.content.length + ' / ' + textareaMaxLength }}</div>
                        <vuelidate-error
                            :type="['required', 'minLength']"
                            :name="$t('mentor_request.request.label')"
                            :model="v$.mentorRequest.content"
                        />
                        <modal :show="showRequestExample" @close="showRequestExample = false">
                            <template #title><slot name="request-modal-title"/></template>
                            <template #content><slot name="request-modal-content"/></template>
                        </modal>
                    </div>
                </div>
            </template>
            <div class="flex gap-1">
                <button class="btn btn--secondary btn--lg" v-if="formStep > 1" @click.prevent="changeFormStep('previous')">
                    {{ $t('back')}}
                </button>
                <template v-if="formStep < 3">
                    <button class="btn btn--primary btn--lg" @click.prevent="changeFormStep('next')">
                        {{ $t('onboarding.save_and_continue') }}
                    </button>
                </template>
                <template v-if="formStep === 3">
                    <button class="btn btn--primary btn--lg" @click.prevent="submitRequest()">
                        <!-- "Submit for approval" -->
                        {{ $t('onboarding.submit_for_approval') }}
                    </button>
                </template>
            </div>
        </template>
        <Flash v-if="flash.message" :type="flash.type ? flash.type : 'info'" :message="flash.message"/>
    </div>
</template>

<script>
import Modal from "src/views/common/modal";

import {MY_VENTURES, MY_VENTURES_MATCHING_MENTOR} from "src/graphql/ventures";
import { VC4A_VARS } from "src/shared/utils";
import {
    filterTerms,
    getTermName,
    getTermsForTaxonomy
} from "src/shared/terms";
import {CREATE_MENTOR_REQUEST, GET_MENTOR_REQUEST} from "src/graphql/mentor-request";
import Flash from "src/views/common/flash";
import {
    required,
    minLength,
    minValue
} from '@vuelidate/validators'
import useVuelidate from "@vuelidate/core";
import VuelidateError from "src/views/forms/vuelidate-error";
import i18n from "src/i18n";
import LoaderCluster from "src/views/loaders/loader-cluster";
import Vc4aSelect from "src/views/utility/vc4a-select";
import Alert from "src/views/common/alert";

export default {
    name: "MentorRequestForm",
    props: {
        formStep: Number,
        menteeId: [Number, String],
        mentorId: [Number, String],
        mentorSlug: String,
        mentorRequestId: [Number, String],
        isNewMentorRequest: [Boolean],
        userId: [Number, String],
        partnerId: [Number, String]
    },
    components: {
        Vc4aSelect,
        Alert,
        Modal,
        Flash,
        VuelidateError,
        LoaderCluster
    },
    setup () {
        return {
            v$: useVuelidate()
        }
    },
    data() {
        return {
            errorMessage: '',
            isLoading: true,
            loadingVentures: true,
            mentorRequest: {
                mentee_id: 0,
                mentor_id: 0,
                venture_id: null,
                title: '',
                content: '',
            },
            selectedVentureData: null,
            mentorMeta: {
                introduction: '',
                problem: '',
            },
            mentorTerms: {
                topic: []
            },
            account: {
                ventures: []
            },
            ventures: [],
            disallowedVenturesList: [],
            topic: [],
            showIntroExample: false,
            showProblemExample: false,
            showRequestExample: false,
            flash: {
                message: '',
                type: ''
            },
            titleMaxLength: 80,
            introMinLength: 100,
            problemMinLength: 250,
            requestMinLength: 250,
            textareaMaxLength: 2000,
            showRequirementAlert: false,
            requirementAlertData: null,
        }
    },
    validations () {
        return {
            userIsSignedIn: {
                required: () => {
                    return this.userIsSignedIn
                }
            },
            mentorRequest: {
                title: {required},
                content: {
                    required,
                    minLength: minLength(this.requestMinLength),
                },
                venture_id: {
                    required: (val) => {
                        return !!Number(val);
                    }
                }
            },
            selectedVentureData: {
                // Validate the selected venture - Allow selection when it has a logo.
                has_logo: {
                    required: () => {
                        return this.selectedVentureData.has_logo;
                    }
                }
            },
            mentorTerms: {
                topic: {required}
            },
            mentorMeta: {
                introduction: {
                    required,
                    minLength: minLength(this.introMinLength),
                },
                problem: {
                    required,
                    minLength: minLength(this.problemMinLength)
                }
            }
        }
    },
    watch: {
        loadingVentures(newVal) {
            // If loadingVentures = false (ventures are loaded)
            if (this.ventures.length && !newVal) {
                this.selectedVentureData = this.ventures.find(
                    venture => venture.venture_id === this.mentorRequest.venture_id
                );

                // Pre-selects the first venture (if no venture is selected)
                if (!this.mentorRequest.venture_id) {
                    this.mentorRequest.venture_id = this.ventures[0].venture_id;
                }
            }
        },
        'mentorRequest.venture_id'(newValue) {
            // Update value on change
            this.selectedVentureData = this.ventures.find(
                venture => venture.venture_id === newValue
            );
        }
    },
    mounted() {

        // Run queries ONLY when user is signed in.
        if( parseInt(this.userId) ) {
            this.getMenteeVentures();
            this.getMentorRequest();
            this.getTermsForCurrentRequest();
            getTermsForTaxonomy( 'topic', 1, 1000, 0, false ).then( terms => {
                this.topic = {...filterTerms( terms, false, 'name', false )};
            } );
            
            this.mentorRequest.mentee_id = parseInt( this.menteeId )
            this.mentorRequest.mentor_id = parseInt( this.mentorId )
        } else {
            this.isLoading = false;
        }

        this.$watch('isLoading', function (newVal) {
            this.$emit('loading-status-changed', newVal);
        })
    },
    computed: {
        getNewVentureUrl() {
            return document.location.protocol + '//'
                + VC4A_VARS.VC4A_SITE_URL
                + '/ventures/add/';
        },
        loginUrl() {
            return document.location.protocol + '//'
                + VC4A_VARS.VC4A_SITE_URL
                + '/login/?redirect_to='
                + encodeURIComponent( document.location.href );
        },
        userHasVentureProfile() {
            return this.ventures.length > 0;
        },
        userIsSignedIn() {
            return this.account.userId > 0;
        },
    },
    methods: {
        getMenteeVentures() {
            let allowedVentures = [];

            this.$apollo.query({
                query: MY_VENTURES_MATCHING_MENTOR,
                variables: {
                    where: {
                        userId: parseInt( this.menteeId ),
                        mentorId: parseInt( this.mentorId ),
                    },
                },
            }).then(({data, loading}) => {
                if( ! loading ) {
                    this.ventures = [];
                    if( data.venturesForMentor.edges.length > 0 ) {
                        data.venturesForMentor.edges.map( item => {
                            this.ventures.push(item.node);
                            allowedVentures.push(item.node.venture_id);
                            return item.node;
                        } );
                    }
                }
            }).catch((error) => {
                this.errorMessage = error.message;
                this.loadingVentures = false;
                this.isLoading = false;
            }).finally(() => {
                this.$apollo.query({
                    query: MY_VENTURES,
                }).then(({data, loading}) => {
                    if( ! loading ) {
                        this.isLoading = false;
                        this.loadingVentures = false;
                        this.account = {...data.account};
                        if( data.account.ventures.nodes ) {
                            data.account.ventures.nodes.map( item => {
                                if( ! allowedVentures.includes( item.venture_id ) ) {
                                    this.disallowedVenturesList.push(item);
                                }
                                return item;
                            } );
                        }
                    }
                }).catch((error) => {
                    this.errorMessage = error.message;
                    this.loadingVentures = false;
                    this.isLoading = false;
                })
            });
        },
        async changeFormStep( direction ) {
            if (direction === 'next') {
                let formIsCorrect;
                switch (this.formStep) {
                    case 1:
                        formIsCorrect =
                            await(this.v$.userIsSignedIn.$validate()) &&
                            await(this.v$.mentorRequest.venture_id.$validate()) &&
                            await(this.v$.selectedVentureData.has_logo.$validate())
                        break;
                    case 2:
                        formIsCorrect =
                            await(this.v$.mentorTerms.topic.$validate())
                        break;
                    case 3:
                        formIsCorrect =
                            await(this.v$.mentorRequest.title.$validate()) &&
                            await(this.v$.mentorRequest.content.$validate()) &&
                            await(this.v$.mentorMeta.introduction.$validate()) &&
                            await(this.v$.mentorMeta.problem.$validate())
                        break;
                }
                
                if(formIsCorrect) {
                    if (this.formStep === 1) this.showRequirementAlert = false
                    this.$emitter.$emit( 'change_form_step', direction );
                } else {
                    this.requirementAlertError();
                }
            } else {
                this.$emitter.$emit( 'change_form_step', direction );
            }
        },
        getTermName( term ) {
            return getTermName( term );
        },
        async submitRequest() {
            let formIsValid = await this.v$.$validate();
            if (formIsValid) {
                this.createMentorRequest();
            } else {
                this.displayFlash({
                    message: i18n.global.t( 'form_errors' ),
                    type: 'danger'
                });
            }
        },
        requirementAlertError() {
            if (!this.userIsSignedIn) {
                this.requirementAlertData = {
                    message: [
                        i18n.global.t( 'error_message.mentor_request.sign_in' ),
                        i18n.global.t( 'error_message.mentor_request.no_venture_profile' )
                    ]
                }
            } else if (!this.mentorRequest.venture_id) {
                this.requirementAlertData = {
                    message: [
                        i18n.global.t( 'error_message.mentor_request.select_venture_profile' )
                    ]
                }
            } else if (!this.selectedVentureData.has_logo) {
                this.requirementAlertData = {
                    message: [
                        i18n.global.t( 'error_message.mentor_request.your_venture' ) +
                        ' <strong>' +
                        this.selectedVentureData.title +
                        '</strong> ' +
                        i18n.global.t( 'error_message.mentor_request.venture_req' )
                    ],
                    cta: {
                        text: i18n.global.t( 'error_message.mentor_request.update_venture' ),
                        url: this.selectedVentureData.permalink
                    }
                }
            }
            this.scrollToFormTop();
        },
        scrollToFormTop() {
            let offset = 64, //Arbitrary value to show some spacing before alert
                formTop = this.$refs["mentor-request-form"].offsetTop - offset;
            
            //Scroll only if alert isn't properly visible
            if (window.scrollY >= formTop) {
                window.scrollTo({
                    top: formTop,
                    behavior: 'smooth'
                });
            }
        },
        
        createMentorRequest() {
            let data = {
                clientMutationId: 'createMentorRequestId',
                ...this.mentorRequest
            };
            data.terms = JSON.stringify( this.mentorTerms );
            data.meta = JSON.stringify( this.mentorMeta );
            data.partner_id = parseInt( this.partnerId || 0 );

            // Just in case this is returned by gql query, let's delete it.
            delete data.__typename;
            this.$apollo.mutate({
                mutation: CREATE_MENTOR_REQUEST,
                variables: {
                    input: data,
                },
            }).then((data) => {
                const args = this.isNewMentorRequest ? "onboarding=success" : "edit_status=success";

                this.queryInProgress = false;
                this.redirectTo(data.data.createMentorRequest.permalink + '?' + args);
            }).catch((error) => {
                // Error.
                this.queryInProgress = false;
                this.displayFlash({
                    message: error.message.replace('GraphQL error:', ''),
                    type: 'warning'
                });
            })
        },
        displayFlash( flash ) {
            this.flash = flash;
            setTimeout(() => this.flash = { message: '', type: 'info' }, 3000 );
        },
        redirectTo(link) {
            window.location.href = link;
        },
        getMentorRequest() {
            if( parseInt( this.mentorRequestId ) < 1  ) {
                return;
            }
            let variables = {
                id: parseInt( this.mentorRequestId ),
                idType: 'database_id'
            };
            this.$apollo.query({
                query: GET_MENTOR_REQUEST,
                variables: variables,
            }).then((result) => {
                if( result.data.mentorRequest !== null ) {
                    this.mentorRequest = {...result.data.mentorRequest};
                    this.mentorMeta.problem = result.data.mentorRequest.problem;
                    this.mentorMeta.introduction = result.data.mentorRequest.introduction;
                }
                this.isLoading = false;
            }).catch((error) => {
                console.error(error)
            })
        },
        getTermsForCurrentRequest() {
            if( parseInt( this.mentorRequestId ) < 1 ) {
                return;
            }
            Object.keys(this.mentorTerms).forEach( taxonomy => {
                getTermsForTaxonomy(taxonomy, 1, 1000, parseInt( this.mentorRequestId ), false).then(terms => {
                    this.mentorTerms[taxonomy] = [...filterTerms(terms, false, false)];
                })
            } );
        },
        
        blurInput(inputName){
            switch (inputName) {
                case 'introduction':
                    this.mentorMeta.introduction = this.removeExcessWhitespace(this.mentorMeta.introduction);
                    this.v$.mentorMeta.introduction.$touch();
                    break;
                    
                case 'problem':
                    this.mentorMeta.problem = this.removeExcessWhitespace(this.mentorMeta.problem);
                    this.v$.mentorMeta.problem.$touch();
                    break;
                    
                case 'request':
                    this.mentorRequest.content = this.removeExcessWhitespace(this.mentorRequest.content);
                    this.v$.mentorRequest.content.$touch();
                    break;
            }
        },
        removeExcessWhitespace(text) {
            return text
                .trim() // Remove whitespace at the beginning and end of the string
                .replace(/  +/g, " ") // Replaces multiple spaces (2+) in the string with a single one
                .replace(/\n\s*\n\s*\n/g, "\n\n"); // Replaces (3+ line breaks with 2 line breaks).
        }
    }
}
</script>
